import { ChevronDown, ChevronUp, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { FormActions, FormField, FormInput, FormSelect, FormTextarea, useToast } from "@/components/shared";
import { extensionService } from "@/lib/services/extensions";
import { teamService } from "@/lib/services/teams";
import type { ExtensionProfileCreate, ExtensionProfileResponse, ExtensionProfileUpdate, Team } from "@/types";

interface ProfilesEditorProps {
  extensionUuid: string;
  profiles: ExtensionProfileResponse[];
  onUpdate: () => void;
}

export function ProfilesEditor({ extensionUuid, profiles, onUpdate }: Readonly<ProfilesEditorProps>) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const { showToast } = useToast();

  const loadTeams = async () => {
    if (teamsLoaded) return;
    try {
      const res = await teamService.list();
      setTeams(res.data || []);
      setTeamsLoaded(true);
    } catch {
      // ignore
    }
  };

  const handleCreate = async (data: ExtensionProfileCreate) => {
    try {
      await extensionService.createProfile(extensionUuid, data);
      setIsCreating(false);
      showToast("Profile created successfully", "success");
      onUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to create profile";
      showToast(msg, "error");
    }
  };

  const handleUpdate = async (profileUuid: string, data: ExtensionProfileUpdate) => {
    try {
      await extensionService.updateProfile(extensionUuid, profileUuid, data);
      setEditingUuid(null);
      showToast("Profile updated", "success");
      onUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to update profile";
      showToast(msg, "error");
    }
  };

  const handleDelete = async (profileUuid: string) => {
    try {
      await extensionService.deleteProfile(extensionUuid, profileUuid);
      setDeletingUuid(null);
      showToast("Profile deleted", "success");
      onUpdate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to delete profile";
      showToast(msg, "error");
    }
  };

  // Teams that don't already have a profile
  const availableTeams = teams.filter((t) => !profiles.some((p) => p.teamUuid === t.uuid));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">
          Profiles provide team-specific context and ontology for this extension's skills. Each team can have its own
          data descriptions, query patterns, and instructions.
        </p>
        <button
          onClick={() => {
            loadTeams();
            setIsCreating(true);
          }}
          className="flex items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-600 transition-colors"
        >
          <Plus size={14} />
          Add Profile
        </button>
      </div>

      {/* Create form */}
      {isCreating && (
        <ProfileForm teams={availableTeams} onSubmit={handleCreate} onCancel={() => setIsCreating(false)} />
      )}

      {/* Profile list */}
      {profiles.length === 0 && !isCreating && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-6 text-center">
          <Users size={24} className="mx-auto mb-2 text-slate-500" />
          <p className="text-sm text-slate-400">No profiles configured.</p>
          <p className="text-xs text-slate-500 mt-1">
            Add a profile to customize this extension's behavior for a specific team.
          </p>
        </div>
      )}

      {profiles.map((profile) => (
        <div key={profile.uuid} className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-hidden">
          {/* Profile header */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className={`h-2 w-2 rounded-full ${profile.isEnabled ? "bg-emerald-400" : "bg-slate-500"}`} />
              <div>
                <span className="text-sm font-medium text-slate-200">{profile.name}</span>
                <span className="ml-2 text-xs text-slate-500">({profile.teamName})</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setExpandedUuid(expandedUuid === profile.uuid ? null : profile.uuid)}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                title={expandedUuid === profile.uuid ? "Collapse" : "Expand"}
              >
                {expandedUuid === profile.uuid ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <button
                onClick={() => {
                  loadTeams();
                  setEditingUuid(profile.uuid);
                  setExpandedUuid(profile.uuid);
                }}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                title="Edit"
              >
                <Pencil size={14} />
              </button>
              {deletingUuid === profile.uuid ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleDelete(profile.uuid)}
                    className="rounded px-2 py-1 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setDeletingUuid(null)}
                    className="rounded px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setDeletingUuid(profile.uuid)}
                  className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Expanded content */}
          {expandedUuid === profile.uuid && (
            <div className="border-t border-slate-700 px-4 py-3">
              {editingUuid === profile.uuid ? (
                <ProfileEditForm
                  profile={profile}
                  onSubmit={(data) => handleUpdate(profile.uuid, data)}
                  onCancel={() => setEditingUuid(null)}
                />
              ) : (
                <div className="space-y-2">
                  {profile.description && <p className="text-xs text-slate-400">{profile.description}</p>}
                  <div className="max-h-64 overflow-y-auto rounded bg-slate-900 p-3">
                    <pre className="whitespace-pre-wrap text-xs text-slate-300 font-mono">
                      {profile.contextInstructions || "(empty)"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function ProfileForm({
  teams,
  onSubmit,
  onCancel,
}: Readonly<{
  teams: Team[];
  onSubmit: (data: ExtensionProfileCreate) => void;
  onCancel: () => void;
}>) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [teamUuid, setTeamUuid] = useState("");
  const [contextInstructions, setContextInstructions] = useState("");

  const canSubmit = name.trim() && teamUuid;

  return (
    <div className="rounded-lg border border-slate-600 bg-slate-800 p-4 space-y-3">
      <h4 className="text-sm font-medium text-slate-200">New Profile</h4>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Name" htmlFor="profile-name">
          <FormInput id="profile-name" value={name} onChange={setName} placeholder="e.g., Sales Team Fabric" />
        </FormField>
        <FormField label="Team" htmlFor="profile-team">
          <FormSelect
            id="profile-team"
            value={teamUuid}
            onChange={setTeamUuid}
            placeholder="Select a team..."
            options={teams.map((t) => ({ value: t.uuid, label: t.name }))}
          />
        </FormField>
      </div>

      <FormField label="Description (optional)" htmlFor="profile-description">
        <FormInput
          id="profile-description"
          value={description}
          onChange={setDescription}
          placeholder="Brief description of this profile's purpose"
        />
      </FormField>

      <FormField label="Context Instructions (ontology, data descriptions, query patterns)" htmlFor="profile-context">
        <FormTextarea
          id="profile-context"
          value={contextInstructions}
          onChange={setContextInstructions}
          placeholder="Describe the data this team works with, key tables, column meanings, example queries..."
          rows={10}
          mono
          className="resize-y"
        />
      </FormField>

      <FormActions
        onCancel={onCancel}
        onSubmit={() =>
          canSubmit &&
          onSubmit({
            name: name.trim(),
            description: description.trim() || undefined,
            contextInstructions,
            teamUuid,
          })
        }
        submitLabel="Create Profile"
        disabled={!canSubmit}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit form
// ---------------------------------------------------------------------------

function ProfileEditForm({
  profile,
  onSubmit,
  onCancel,
}: Readonly<{
  profile: ExtensionProfileResponse;
  onSubmit: (data: ExtensionProfileUpdate) => void;
  onCancel: () => void;
}>) {
  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description || "");
  const [contextInstructions, setContextInstructions] = useState(profile.contextInstructions);
  const [isEnabled, setIsEnabled] = useState(profile.isEnabled);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Name" htmlFor="edit-profile-name">
          <FormInput id="edit-profile-name" value={name} onChange={setName} />
        </FormField>
        <div className="flex items-end gap-2">
          <div className="flex items-center gap-2">
            <input
              id="edit-profile-enabled"
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="edit-profile-enabled" className="text-xs text-slate-400">
              Enabled
            </label>
          </div>
        </div>
      </div>

      <FormField label="Description" htmlFor="edit-profile-description">
        <FormInput id="edit-profile-description" value={description} onChange={setDescription} />
      </FormField>

      <FormField label="Context Instructions" htmlFor="edit-profile-context">
        <FormTextarea
          id="edit-profile-context"
          value={contextInstructions}
          onChange={setContextInstructions}
          rows={12}
          mono
          className="resize-y"
        />
      </FormField>

      <FormActions
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name: name.trim(),
            description: description.trim() || undefined,
            contextInstructions,
            isEnabled,
          })
        }
        submitLabel="Save Changes"
      />
    </div>
  );
}
